import React, { useEffect, useState } from 'react';
import { Button, Grid2 as Grid, Box, useMediaQuery } from '@mui/material';
import { Add, Delete, Edit, Save, Undo } from '@mui/icons-material';
import { Actions, useDispatchProvider, useStateFormOrder, useStateFormProduct } from '../utils/StateProvider';
import SkeletonForm from '../utils/SkeletonForm';
import { addDoc, collection, deleteDoc, doc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../../config/firebase';
import { useForm } from 'react-hook-form';
import FieldProductStatus from '../fields/FieldProductStatus';
import FieldTextarea from '../fields/FieldTextArea';
import { formatDateTime, getNewDateTime, parseDateTime } from '../../utils/utils';
import FieldText from '../fields/FieldText';
import FieldImage from '../fields/FieldImage';
import FieldCurrency from '../fields/FieldCurrency';
import { createPortal } from 'react-dom';
import FieldDateTime from '../fields/FieldDateTime';
import { usePermissions } from '../utils/AclProvider';
import FieldSupplierAutocomplete from '../fields/FieldSupplierAutocomplete';

const FormProduct = ({ portalRef }) => {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const permissions = usePermissions();
  const stateFormProduct = useStateFormProduct();
  const stateFormOrder = useStateFormOrder();
  const dispatch = useDispatchProvider();

  const handleDialogDelete = () => {
    dispatch &&
      dispatch({
        type: Actions.DialogDelete,
        payload: { open: true, title: 'Are you sure you want to delete this customer?', onConfirm: deleteProduct },
      });
  };

  const [stateForm, setStateForm] = useState('create');
  const [stateLoading, setStateLoading] = useState(true);
  const [stateSaving, setStateSaving] = useState(false);

  const productDocRef = stateFormProduct ? doc(db, 'products', stateFormProduct) : null;
  const productsCollectionRef = collection(db, 'products');
  const orderDocRef = stateFormOrder ? doc(db, 'orders', stateFormOrder) : null;

  const { control, getValues, watch, reset, setValue } = useForm({
    defaultValues: {
      description: '',
      price: '0',
      supplierRef: '',
      orderRef: '',
      imageUrl: '',
      productStatus: 'new',
      dateCreated: formatDateTime(getNewDateTime()),
      dateOrdered: '',
      dateReceived: '',
      dateUpdated: '',
      dateBackorder: '',
      userCreated: '',
      userOrdered: '',
      userReceived: '',
      userUpdated: '',
      userBackorder: '',
    },
  });

  const fetchProduct = async () => {
    if (stateFormProduct === '') {
      reset();
      setStateForm('create');
      return;
    }

    try {
      const docSnap = await getDoc(productDocRef);

      if (docSnap.exists()) {
        setStateForm('edit');
        const productData = docSnap.data();
        const supplierSnap = await getDoc(productData.supplierRef);
        // setValue('customerRef', customerSnap.id);
        setValue('description', productData.description);
        setValue('price', productData.price);
        setValue('imageUrl', productData.imageUrl);
        setValue('supplierRef', supplierSnap.id);
        setValue('productStatus', productData.productStatus);
        setValue('dateCreated', formatDateTime(productData.dateCreated));
        setValue('dateOrdered', formatDateTime(productData.dateOrdered));
        setValue('dateReceived', formatDateTime(productData.dateReceived));
        setValue('dateUpdated', formatDateTime(productData.dateUpdated));
        setValue('dateBackorder', formatDateTime(productData.dateBackorder));
        setValue('userCreated', productData.userCreated);
        setValue('userOrdered', productData.userOrdered);
        setValue('userReceived', productData.userReceived);
        setValue('userUpdated', productData.userUpdated);
        setValue('userBackorder', productData.userBackorder);
      } else {
        dispatch &&
          dispatch({
            type: Actions.Snackbar,
            payload: { open: true, severity: 'error', message: `No product found!` },
          });
      }
    } catch (err) {
      dispatch &&
        dispatch({
          type: Actions.Snackbar,
          payload: { open: true, severity: 'error', message: `Error fetching product: ${err.message}` },
        });
    }
  };

  const editProduct = () => {
    setStateForm('save');
  };

  const revertOrder = () => {
    setStateForm('edit');
    fetchProduct().then();
  };
  // const onSubmit = (data) => {
  //   console.log(data);
  // };
  const addProduct = async () => {
    // await handleSubmit(onSubmit)();
    try {
      setStateSaving(true);
      const productForm = getValues();
      const supplierDocRef = doc(db, 'suppliers', productForm.supplierRef);
      const newProductDocRef = await addDoc(productsCollectionRef, {
        ...productForm,
        orderRef: orderDocRef,
        supplierRef: supplierDocRef,
        dateCreated: parseDateTime(productForm.dateCreated),
        userCreated: auth.currentUser.displayName,
      }).then((productDocRef) => {
        setStateForm('edit');
        setStateSaving(false);

        dispatch &&
          dispatch({
            type: Actions.DialogProduct,
            payload: false,
          });

        dispatch &&
          dispatch({
            type: Actions.FormProduct,
            payload: productDocRef.id,
          });

        dispatch &&
          dispatch({
            type: Actions.Snackbar,
            payload: {
              open: true,
              severity: 'success',
              message: `Successfully added new product: ${productForm.type}`,
            },
          });
      });
    } catch (err) {
      setStateSaving(false);
      dispatch &&
        dispatch({
          type: Actions.Snackbar,
          payload: { open: true, severity: 'error', message: `Error adding new product: ${err.message}` },
        });
    }
  };

  const saveProduct = async () => {
    try {
      setStateSaving(true);
      const productForm = getValues();
      delete productForm.dateCreated;
      delete productForm.userCreated;
      delete productForm.orderRef;
      const supplierDocRef = doc(db, 'suppliers', productForm.supplierRef);
      await updateDoc(productDocRef, {
        ...productForm,
        supplierRef: supplierDocRef,
        dateUpdated: getNewDateTime(),
        userUpdated: auth.currentUser.displayName,
        ...(productForm.productStatus === 'received'
          ? {
              dateReceived: getNewDateTime(),
              userReceived: auth.currentUser.displayName,
            }
          : {
              dateReceived: parseDateTime(productForm.dateReceived),
            }),
        ...(productForm.productStatus === 'ordered'
          ? {
              dateOrdered: getNewDateTime(),
              userOrdered: auth.currentUser.displayName,
            }
          : {
              dateOrdered: parseDateTime(productForm.dateOrdered),
            }),
        ...(productForm.productStatus === 'backorder'
          ? {
              dateBackorder: parseDateTime(productForm.dateBackorder),
              userBackorder: auth.currentUser.displayName,
            }
          : {
              dateBackorder: parseDateTime(productForm.dateBackorder),
            }),
      }).then(() => {
        setStateForm('edit');
        setStateSaving(false);
        dispatch &&
          dispatch({
            type: Actions.Snackbar,
            payload: { open: true, severity: 'success', message: `Successfully update product: ${productForm.type}` },
          });
        dispatch &&
          dispatch({
            type: Actions.DialogProduct,
            payload: false,
          });
      });
    } catch (err) {
      setStateSaving(false);
      dispatch &&
        dispatch({
          type: Actions.Snackbar,
          payload: { open: true, severity: 'error', message: `Error saving product: ${err.message}` },
        });
    }
  };

  const deleteProduct = async () => {
    try {
      setStateSaving(true);
      await deleteDoc(productDocRef).then(() => {
        setStateSaving(false);
        if (dispatch) {
          dispatch({
            type: Actions.Snackbar,
            payload: { open: true, severity: 'success', message: 'Successfully delete customer' },
          });

          dispatch({
            type: Actions.FormProduct,
            payload: '',
          });

          dispatch({
            type: Actions.DialogDelete,
            payload: { open: false, title: '', onConfirm: () => {} },
          });

          dispatch({
            type: Actions.DialogProduct,
            payload: false,
          });
        }
      });
    } catch (err) {
      dispatch({
        type: Actions.Snackbar,
        payload: { open: true, severity: 'error', message: `Error deleting customer: ${err}` },
      });
    }
  };

  const watchProductStatus = watch('productStatus');

  useEffect(() => {
    setStateLoading(true);
    fetchProduct().then(() => {
      setStateLoading(false);
    });
  }, [stateFormProduct]);

  return (
    <Box component={'form'}>
      <>
        {stateLoading ? (
          <SkeletonForm />
        ) : (
          <Grid
            container
            spacing={3}
          >
            <Grid size={12}>
              <FieldTextarea
                label={'Description'}
                name={'description'}
                control={control}
                rows={10}
                disabled={stateForm === 'edit'}
              />
            </Grid>
            <Grid size={12}>
              <FieldSupplierAutocomplete
                label={'Supplier'}
                name={'supplierRef'}
                control={control}
                disabled={stateForm === 'edit'}
              />
            </Grid>
            <Grid size={12}>
              {/*<FieldText*/}
              {/*  label={'Price'}*/}
              {/*  name={'price'}*/}
              {/*  control={control}*/}
              {/*  disabled={stateForm === 'edit'}*/}
              {/*/>*/}
              <FieldCurrency
                label={'Price'}
                name={'price'}
                control={control}
                disabled={stateForm === 'edit'}
                setValue={setValue}
              />
            </Grid>
            <Grid size={12}>
              <FieldImage
                label={'Image'}
                name={'imageUrl'}
                control={control}
                disabled={stateForm === 'edit'}
                setValue={setValue}
                getValues={getValues}
                watch={watch}
              />
            </Grid>
            <Grid size={12}>
              <FieldProductStatus
                label={'Status'}
                name={'productStatus'}
                control={control}
                disabled={stateForm === 'edit' || stateForm === 'create'}
              />
            </Grid>
            <Grid size={12}>
              <Grid
                container
                spacing={2}
              >
                <Grid size={6}>
                  <FieldDateTime
                    label={'Date created'}
                    name={'dateCreated'}
                    control={control}
                    setValue={setValue}
                    disabled={true}
                    disableClear={true}
                    // disabled={stateForm === 'edit'}
                  />
                </Grid>
                <Grid size={6}>
                  <FieldText
                    label={'User created'}
                    name={'userCreated'}
                    control={control}
                    disabled={true}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid size={12}>
              <Grid
                container
                spacing={2}
              >
                <Grid size={6}>
                  <FieldDateTime
                    label={'Date ordered'}
                    name={'dateOrdered'}
                    control={control}
                    setValue={setValue}
                    disabled={true}
                    disableClear={true}
                    // disabled={stateForm === 'edit'}
                  />
                </Grid>
                <Grid size={6}>
                  <FieldText
                    label={'User ordered'}
                    name={'userOrdered'}
                    control={control}
                    disabled={true}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid size={12}>
              <Grid
                container
                spacing={2}
              >
                <Grid size={6}>
                  <FieldDateTime
                    label={'Date received'}
                    name={'dateReceived'}
                    control={control}
                    setValue={setValue}
                    disabled={true}
                    disableClear={true}
                    // disabled={stateForm === 'edit'}
                  />
                </Grid>
                <Grid size={6}>
                  <FieldText
                    label={'User received'}
                    name={'userReceived'}
                    control={control}
                    disabled={true}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid size={12}>
              <Grid
                container
                spacing={2}
              >
                <Grid size={6}>
                  <FieldDateTime
                    label={'Date updated'}
                    name={'dateUpdated'}
                    control={control}
                    disabled={true}
                    disableClear={true}
                  />
                </Grid>
                <Grid size={6}>
                  <FieldText
                    label={'User updated'}
                    name={'userUpdated'}
                    control={control}
                    disabled={true}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid size={12}>
              <Grid
                container
                spacing={2}
              >
                <Grid size={6}>
                  <FieldDateTime
                    label={'Date backorder'}
                    name={'dateBackorder'}
                    control={control}
                    setValue={setValue}
                    disabled={
                      stateForm === 'edit' || watchProductStatus !== 'backorder' || !permissions?.products.delete
                    }
                    // disabled={stateForm === 'edit'}
                    disableClear={true}
                  />
                </Grid>
                <Grid size={6}>
                  <FieldText
                    label={'User backorder'}
                    name={'userBackorder'}
                    control={control}
                    disabled={true}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}
        {portalRef.current &&
          createPortal(
            <>
              <Grid
                container
                spacing={3}
                justifyContent="flex-end"
              >
                {stateForm === 'edit' && permissions?.products?.update && (
                  <Grid size={'auto'}>
                    <Button
                      color={'primary'}
                      variant={'contained'}
                      startIcon={<Edit />}
                      onClick={editProduct}
                      size={isMobile ? 'small' : 'large'}
                      loading={stateSaving}
                    >
                      Edit
                    </Button>
                  </Grid>
                )}
                {stateForm === 'save' && permissions?.products?.update && (
                  <Grid size={'auto'}>
                    <Button
                      color={'primary'}
                      variant={'text'}
                      startIcon={<Undo />}
                      onClick={revertOrder}
                      size={isMobile ? 'small' : 'large'}
                      disabled={stateSaving}
                    >
                      Undo
                    </Button>
                  </Grid>
                )}
                {stateForm === 'save' && permissions?.products?.delete && (
                  <Grid size={'auto'}>
                    <Button
                      color={'primary'}
                      variant={'text'}
                      startIcon={<Delete />}
                      onClick={handleDialogDelete}
                      size={isMobile ? 'small' : 'large'}
                      disbaled={stateSaving}
                    >
                      Delete
                    </Button>
                  </Grid>
                )}
                {stateForm === 'save' && permissions?.products?.update && (
                  <Grid size={'auto'}>
                    <Button
                      color={'primary'}
                      variant={'contained'}
                      startIcon={<Save />}
                      onClick={saveProduct}
                      size={isMobile ? 'small' : 'large'}
                      loading={stateSaving}
                    >
                      Save
                    </Button>
                  </Grid>
                )}
                {stateForm === 'create' && permissions?.products?.create && (
                  <Grid size={'auto'}>
                    <Button
                      color={'primary'}
                      variant={'contained'}
                      startIcon={<Add />}
                      onClick={addProduct}
                      size={isMobile ? 'small' : 'large'}
                      loading={stateSaving}
                    >
                      Create
                    </Button>
                  </Grid>
                )}
              </Grid>
            </>,
            portalRef.current,
          )}
      </>
    </Box>
  );
};

export default FormProduct;
